import logger from '../../log';
import { ProxyModifier } from './Modifier';
import { HttpRequest, HttpResponse } from '../interfaces';
import { isManifestUrl } from '../../common/utils/sourceTypeUtils';
import { ProxyConfig } from '../../config/ProxyConfig';
import { PlayType } from '../../common/constants';
import { SourceType } from '../../webrtc/agent/interfaces';

const log = logger.getLogger(__filename);

export type MediaType = 'manifest' | 'segment';

const HOURS_12 = '43200';
const SECONDS_2 = '2';

export function getMaxAge(
  app: string,
  mediaType: MediaType,
  playType: PlayType
): string | undefined {
  // To be used when SRV-2237 is implemented.
  switch (app) {
    case 'microsoft.stream':
    case 'microsoft.teams':
    case 'kollective.webcaster':
    case 'intrado.studio':
      if (playType == PlayType.VOD) {
        return HOURS_12;
      } else {
        return mediaType == 'manifest' ? SECONDS_2 : HOURS_12;
      }
    default:
      return undefined;
  }
}

export default class EdgeCacheModifier implements ProxyModifier {
  app: string;
  modifiedBaseUrl: string;
  proxyConfig: ProxyConfig;
  sdkVer: string;
  sessionToken: string;
  playType: PlayType;

  constructor({
    playType,
    proxyConfig,
    app,
    sdkVer,
    sessionToken
  }: {
    proxyConfig: ProxyConfig;
    app: string;
    sdkVer: string;
    sessionToken: string;
    playType: PlayType;
  }) {
    this.app = app;
    this.proxyConfig = proxyConfig;
    this.sdkVer = sdkVer;
    this.playType = playType;
    this.sessionToken = sessionToken;
    this.modifiedBaseUrl = `${this.proxyConfig.scheme}://${this.proxyConfig.host} `;
  }

  modify(request: HttpRequest): HttpRequest {
    const mediaType = isManifestUrl(request.url) ? 'manifest' : 'segment';
    const modifiedUrl = new URL(this.modifiedBaseUrl);
    this.proxyConfig.timeout;

    const origParams = new URL(request.url).searchParams;
    // set original params
    for (const [key, value] of origParams.entries()) {
      modifiedUrl.searchParams.append(key, value);
    }
    const maxAge = getMaxAge(this.app, mediaType, this.playType);
    if (maxAge) {
      request.headers.set('X-Kollective-S-Max-Age', maxAge);
    }
    request.headers.set('X-Kollective-AppName', this.app);
    request.headers.set('X-Kollective-MediaType', mediaType);
    request.headers.set('Authorization', `Bearer ${this.sessionToken}`);
    request.headers.set('X-Kollective-KSDKVersion', this.sdkVer);
    modifiedUrl.searchParams.set('url', request.url);

    return {
      ...request,
      modifiedUrl: modifiedUrl.toString(),
      // proxyConfig timeout is in seconds. Request expects milliseconds
      timeout: this.proxyConfig.timeout * 1000
    };
  }

  getTransferSourceType(response: HttpResponse): SourceType {
    const { headers } = response;
    if (!headers) {
      return SourceType.PROXY;
    }
    const header = Array.from(headers.keys()).find(v => {
      const header = v && v.toLowerCase();
      return header && header === 'x-kollective-proxy-cache';
    });
    const cache = header && headers.get(header);
    if (cache && cache.toLowerCase() == 'hit') {
      return SourceType.PROXY_CACHE;
    } else {
      return SourceType.PROXY;
    }
  }

  async checkStatus(): Promise<void> {
    const url = new URL('healthz', this.modifiedBaseUrl);
    try {
      // timeout the status check with the proxy configs timeout value.
      const controller = new AbortController();
      const signal = controller.signal;
      setTimeout(() => controller.abort(), this.proxyConfig.timeout * 1000);
      const resp = await fetch(url.toString(), { signal });
      log.debug(resp);
      return;
    } catch (e) {
      // if this call fails, proxy config is down.
      this.proxyConfig.disabled = true;
      throw e;
    }
  }
}
